// apps/web/src/components/ContactForm.tsx

"use client"

import { useForm } from "@tanstack/react-form"
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useContactForm } from "@/hooks/use-contact-form"
import {
  type ContactFormData,
  contactValidators,
  parseContactForm,
} from "@/lib/contact-form-schema"
import { cn } from "@/lib/utils"

interface ContactFormProps {
  submitButtonText?: string
  successMessage?: string
}

export function ContactForm({
  submitButtonText = "Send Message",
  successMessage = "Thank you! Your message has been sent successfully.",
}: ContactFormProps) {
  const { isSubmitting, isSuccess, error, submitForm } = useContactForm()

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      message: "",
    } as ContactFormData,
    onSubmit: async ({ value }) => {
      const result = parseContactForm(value)
      if (!result.success) {
        console.error("Validation failed:", result.errors)
        return
      }
      await submitForm(result.data)
    },
  })

  if (isSuccess) {
    return (
      <Card className="rounded-none border-2 border-brand-90 bg-brand-1">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-brand-90">Message Sent!</h3>
            <p className="text-zinc-700">{successMessage}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="rounded-none border-2 border-brand-90 bg-brand-1">
      <CardContent className="p-6">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          {/* Global error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border-2 border-red-500 text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Name Field */}
          <form.Field
            name="name"
            validators={{
              onChange: ({ value }) => contactValidators.name(value),
            }}
          >
            {(field) => (
              <div>
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-brand-90 mb-2"
                >
                  Name *
                </label>
                <Input
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Your name"
                  className={cn(
                    "bg-brand-1 border-2 border-dashed border-brand-50 text-brand-90 placeholder-brand-50 focus-visible:ring-0 focus-visible:border-solid focus-visible:border-brand-60 rounded-none",
                    field.state.meta.errors.length > 0 && "border-red-500 border-solid"
                  )}
                  disabled={isSubmitting}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-700 text-sm mt-1">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )}
          </form.Field>

          {/* Email Field */}
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }) => contactValidators.email(value),
            }}
          >
            {(field) => (
              <div>
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-brand-90 mb-2"
                >
                  Email *
                </label>
                <Input
                  id={field.name}
                  name={field.name}
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="your@email.com"
                  className={cn(
                    "bg-brand-1 border-2 border-dashed border-brand-50 text-brand-90 placeholder-brand-50 focus-visible:ring-0 focus-visible:border-solid focus-visible:border-brand-60 rounded-none",
                    field.state.meta.errors.length > 0 && "border-red-500 border-solid"
                  )}
                  disabled={isSubmitting}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-700 text-sm mt-1">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )}
          </form.Field>

          {/* Message Field */}
          <form.Field
            name="message"
            validators={{
              onChange: ({ value }) => contactValidators.message(value),
            }}
          >
            {(field) => (
              <div>
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-brand-90 mb-2"
                >
                  Message *
                </label>
                <Textarea
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  rows={4}
                  placeholder="Tell me about your project..."
                  className={cn(
                    "bg-brand-1 border-2 border-dashed border-brand-50 text-brand-90 placeholder-brand-50 focus-visible:ring-0 focus-visible:border-solid focus-visible:border-brand-90 resize-none rounded-none",
                    field.state.meta.errors.length > 0 && "border-red-500 border-solid"
                  )}
                  disabled={isSubmitting}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-700 text-sm mt-1">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )}
          </form.Field>

          {/* Submit Button */}
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isFormSubmitting]) => (
              <Button
                type="submit"
                variant="outline"
                size="lg"
                disabled={!canSubmit || isSubmitting || isFormSubmitting}
                className="w-full rounded-none border-2 border-brand-90 bg-brand-90 text-brand-1 hover:bg-[hsl(var(--giof-pink))] hover:text-brand-1 cursor-pointer transition-all"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSubmitting ? "Sending..." : submitButtonText}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </CardContent>
    </Card>
  )
}
